import type { MigrationSummary } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-app-onprem-migrations/v1/message.pb';
import {
  ReleaseStatusAlpha,
  ReleaseStatusAvailable,
  ReleaseStatusBeta,
  ReleaseStatusDeprecated,
  ReleaseStatusGa,
  ReleaseStatusNotAvailable,
  ReleaseStatusUnspecified,
} from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import dayjs from 'dayjs';
import { IconName } from '@frontend/icons';
import { TableColumnConfig } from '@frontend/design-system';
import {
  MigrationStatus,
  MigrationStatusCardConfig,
  MigrationStatusKey,
  MigrationStepStatus,
  MigrationStepType,
  WindowOption,
} from './migrationDashboardTypings';

export enum Verticals {
  DENTAL = 'Dental',
  ORTHO = 'Ortho',
  VET = 'Vet',
  OPTO = 'Opto',
  MEDICAL = 'Medical',
  MEDSPA = 'MedSpa',
  AUDIOLOGY = 'Audiology',
  ALL = 'All',
}

export const verticalsIconNameMap: Record<Verticals, IconName> = {
  [Verticals.DENTAL]: 'tooth',
  [Verticals.ORTHO]: 'bone',
  [Verticals.VET]: 'paw',
  [Verticals.OPTO]: 'view',
  [Verticals.MEDICAL]: 'medical-filled',
  [Verticals.MEDSPA]: 'hourglass',
  [Verticals.AUDIOLOGY]: 'support',
  [Verticals.ALL]: 'analytics',
};

export const verticalEntries: Array<{ vertical: Verticals; iconName: IconName }> = [
  Verticals.ALL,
  ...Object.values(Verticals).filter((vertical) => vertical !== Verticals.ALL),
].map((vertical) => ({ vertical, iconName: verticalsIconNameMap[vertical] }));

export enum Confirmation {
  YES = 'Yes',
  NO = 'No',
}

export enum FeatureStatus {
  SUPPORTED = 'Supported',
  NOT_SUPPORTED = 'Not Supported',
}

export enum FeatureStatusFilter {
  ALL = 'All',
  SUPPORTED = 'Supported',
  NOT_SUPPORTED = 'Unsupported',
}

export const featureStatusFilterIconNameMap: Record<FeatureStatusFilter, IconName> = {
  [FeatureStatusFilter.ALL]: 'analytics',
  [FeatureStatusFilter.SUPPORTED]: 'confirm-badge',
  [FeatureStatusFilter.NOT_SUPPORTED]: 'error-badge',
};

export const featureStatusFilterEntries: Array<{ filter: FeatureStatusFilter; iconName: IconName }> = Object.values(
  FeatureStatusFilter
).map((filter) => ({ filter, iconName: featureStatusFilterIconNameMap[filter] }));

export enum UserRole {
  ENGINEERING = 'ENGINEERING',
  SALES = 'SALES',
  SUPPORT = 'SUPPORT',
  PRODUCT = 'PRODUCT',
  ONBOARDING_CSM_SW = 'ONBOARDING_CSM_SW',
  LEADERSHIP = 'LEADERSHIP',
  ADMIN = 'ADMIN',
  ALL = 'ALL',
}

export enum IntegrationOverviewTab {
  FEATURES = 'Features',
  FAQS = "FAQ's",
  QUICK_LINKS = 'Quick Links',
  EXTERNAL_PARTNER_RESOURCES = 'External Partner Resources',
  PARTNER_INFORMATION = 'Partner Information',
  SUPPORTED_PMS_VERSIONS = 'Supported PMS Versions',
  OPERATING_SYSTEM = 'Operating System',
}

export enum PartnerInformationFields {
  PARENT_COMPANY = 'Parent Company',
  PARTNER_STATUS = 'Partner Status',
  PARTNER_INTEGRATION_STATUS = 'Partner Integration Status',
  AUTHORIZED_INTEGRATION_SINCE = 'Authorized Integration Since',
  PARTNER_DETAILS = 'Partner Details',
}

export enum IntegrationModalType {
  DELETE_CONFIRMATION = 'DELETE_CONFIRMATION',
  FAQ = 'FAQ',
  LINK = 'LINK',
  EDIT_PARTNER_INFORMATION = 'EDIT_PARTNER_INFORMATION',
  EDIT_OS_OR_PMS_VERSION = 'EDIT_OS_OR_PMS_VERSION',
  SUPPORTED_FEATURE = 'SUPPORTED_FEATURE',
  EDIT_SINGLE_FIELD = 'EDIT_SINGLE_FIELD',
}

export enum EditSingleFieldVariant {
  DESCRIPTION = 'description',
  VERTICALS = 'verticals',
  RELEASE_STATUS = 'releaseStatus',
  ROOT_NAME = 'rootName',
}

export const RELEASE_STATUS_OPTIONS = [
  { value: ReleaseStatusUnspecified, label: 'Unspecified' },
  { value: ReleaseStatusAvailable, label: 'Available' },
  { value: ReleaseStatusNotAvailable, label: 'Not Available' },
  { value: ReleaseStatusGa, label: 'GA' },
  { value: ReleaseStatusBeta, label: 'Beta' },
  { value: ReleaseStatusAlpha, label: 'Alpha' },
  { value: ReleaseStatusDeprecated, label: 'Deprecated' },
];

export const REQUEST_CHANGE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeuo6ntI6q_FeLQLr95c-Xh5SQTYmRfJa7TjoFWGJyaVp8hMQ/viewform?usp=header';

/**************************** Migration Dashboard Constants ****************************/

export const MIGRATION_STATE: MigrationStatusCardConfig[] = [
  {
    key: 'completed',
    statusValue: 'COMPLETED',
    label: 'Completed',
    description: 'Cutover successful and monitoring window cleared',
    color: 'var(--fabric-color-status-success-subtle)',
    borderColor: 'var(--fabric-color-status-success-default)',
  },
  {
    key: 'pending',
    statusValue: 'PENDING',
    label: 'Pending',
    description: 'Record created; pre-flight not yet run',
    color: '#F4F5F7',
    borderColor: 'var(--fabric-color-text-muted)',
  },
  {
    key: 'preFlightPassed',
    statusValue: 'PRE_FLIGHT_PASSED',
    label: 'Pre Flight Passed',
    description: 'All compatibility checks succeeded; migration eligible to proceed',
    color: 'var(--fabric-color-action-primary-background-subtle)',
    borderColor: 'var(--fabric-color-action-primary-background-default)',
  },
  {
    key: 'identityReplicated',
    statusValue: 'IDENTITY_REPLICATED',
    label: 'Identity Replicated',
    description: 'Source identity mirrored into integrations-auth-api',
    color: 'var(--fabric-color-status-warning-subtle)',
    borderColor: 'var(--fabric-color-status-warning-default)',
  },
  {
    key: 'engineProvisioned',
    statusValue: 'ENGINE_PROVISIONED',
    label: 'Engine Provisioned',
    description: 'Sync-app-engine binaries downloaded and container registered',
    color: '#FEF4F6',
    borderColor: '#F32C59',
  },
  {
    key: 'cleanedUp',
    statusValue: 'CLEANED_UP',
    label: 'Cleaned Up',
    description: 'Resources cleaned up successfully',
    color: '#E6F5F8',
    borderColor: '#00A3B5',
  },
  {
    key: 'legacyServiceStopped',
    statusValue: 'LEGACY_SERVICE_STOPPED',
    label: 'Legacy Service Stopped',
    description: 'Legacy service stopped successfully (either via cutover or rollback)',
    color: '#F4EBF7',
    borderColor: '#9B33A8',
  },
  {
    key: 'failed',
    statusValue: 'FAILED',
    label: 'Failed',
    description: 'A step failed without automatic rollback',
    color: 'var(--fabric-color-status-error-subtle)',
    borderColor: 'var(--fabric-color-status-error-default)',
  },
  {
    key: 'rolledBack',
    statusValue: 'ROLLED_BACK',
    label: 'Rolled Back',
    description: 'Automatic rollback triggered; legacy service restored',
    color: '#F4F5FE',
    borderColor: '#3348FF',
  },
  {
    key: 'rollbackFailed',
    statusValue: 'ROLLBACK_FAILED',
    label: 'Rollback Failed',
    description:
      'Automatic rollback was triggered but did not complete; legacy service may not be fully restored and manual intervention is required',
    color: '#FFE3E0',
    borderColor: 'var(--fabric-color-status-error-strong)',
  },
  {
    key: 'incompatible',
    statusValue: 'INCOMPATIBLE',
    label: 'Incompatible',
    description:
      'Pre-flight determined the installation cannot be migrated under the current TPD (e.g. Windows <10, insufficient disk, missing service install permissions). Treated as excluded from the migration target rather than counted as a failure. (terminal)',
    color: '#FFF5EB',
    borderColor: '#F57800',
  },
];

export const MIGRATION_STATE_PIE_CHART_COLORS: Record<MigrationStatusKey, string> = {
  completed: 'var(--fabric-color-status-success-default)',
  cleanedUp: '#00A3B5',
  legacyServiceStopped: '#9B33A8',
  pending: 'var(--fabric-color-text-muted)',
  preFlightPassed: 'var(--fabric-color-action-primary-background-default)',
  identityReplicated: 'var(--fabric-color-status-warning-default)',
  engineProvisioned: '#F32C59',
  failed: 'var(--fabric-color-status-error-default)',
  rolledBack: '#3348FF',
  rollbackFailed: 'var(--fabric-color-status-error-strong)',
  incompatible: '#F57800',
};

export const TABLE_COL_CONFIG: TableColumnConfig<MigrationSummary>[] = [
  { id: 'id', Header: 'ID', accessor: 'id', width: 200 },
  { id: 'sourceId', Header: 'Source ID', accessor: 'sourceId', width: 200 },
  { id: 'orgId', Header: 'Org ID', accessor: 'orgId', width: 200 },
  { id: 'locationId', Header: 'Location ID', accessor: 'locationId', width: 200 },
  {
    id: 'status',
    Header: 'Status',
    accessor: 'status',
    width: 200,
    filterConfig: { type: 'checklist', label: 'Status' },
  },
  {
    id: 'createdAt',
    Header: 'Created At',
    accessor: 'createdAt',
    width: 200,
    cellRenderer: (cellValue) => {
      return cellValue ? dayjs(cellValue).format('DD MMM YYYY hh:mm A') : '—';
    },
  },
  {
    id: 'updatedAt',
    Header: 'Updated At',
    accessor: 'updatedAt',
    width: 200,
    cellRenderer: (cellValue) => {
      return cellValue ? dayjs(cellValue).format('DD MMM YYYY hh:mm A') : '—';
    },
  },
];

export const MIGRATION_STEP_META: Record<
  MigrationStepType,
  { label: string; description: string; iconName: IconName }
> = {
  pre_check: {
    label: 'Pre-Check',
    description: 'Compatibility gates: architecture, disk space, service & write permissions.',
    iconName: 'check-please',
  },
  setup: {
    label: 'Setup',
    description:
      'Identity replication, binary deployment, container registration, end-to-end connectivity verification.',
    iconName: 'settings',
  },
  replicate_identity: {
    label: 'Replicate Identity',
    description: 'Identities are replicated without discrepancies.',
    iconName: 'user-management',
  },
  revert_identity: {
    label: 'Revert Identity',
    description: 'Replication is reverted cleanly without discrepancies or residual data.',
    iconName: 'history',
  },
  cutover: {
    label: 'Cutover',
    description: 'Pause legacy sync, start sync-app-engine, swap control plane traffic.',
    iconName: 'data-sync',
  },
  monitoring: {
    label: 'Monitoring',
    description: 'Stability window (≥1 week) with legacy service still present.',
    iconName: 'analytics',
  },
  rollback: {
    label: 'Rollback',
    description: 'Automated restoration of the legacy service on trigger conditions.',
    iconName: 'refresh',
  },
  cleanup: {
    label: 'Cleanup',
    description: 'Removal of legacy service and data directory after monitoring window.',
    iconName: 'trash',
  },
};

export const MIGRATION_STEP_ORDER: MigrationStepType[] = [
  'pre_check',
  'setup',
  'replicate_identity',
  'revert_identity',
  'cutover',
  'monitoring',
  'rollback',
  'cleanup',
];

export const STEP_STATUS_THEME: Record<
  MigrationStepStatus,
  { label: string; color: string; bg: string; iconName: IconName }
> = {
  completed: {
    label: 'Completed',
    color: 'var(--fabric-color-status-success-default)',
    bg: 'var(--fabric-color-status-success-subtle)',
    iconName: 'check',
  },
  running: {
    label: 'Running',
    color: 'var(--fabric-color-action-primary-background-default)',
    bg: 'var(--fabric-color-action-primary-background-subtle)',
    iconName: 'play',
  },
  pending: {
    label: 'Pending',
    color: 'var(--fabric-color-text-muted)',
    bg: '#F4F5F7',
    iconName: 'pending',
  },
  failed: {
    label: 'Failed',
    color: 'var(--fabric-color-status-error-default)',
    bg: 'var(--fabric-color-status-error-subtle)',
    iconName: 'alert-invert',
  },
  skipped: {
    label: 'Skipped',
    color: 'var(--fabric-color-status-warning-default)',
    bg: 'var(--fabric-color-status-warning-subtle)',
    iconName: 'block',
  },
};

export const TARGET_FLEET_SIZE = 40_000;

export const RECENT_FAILURES_WINDOW_OPTIONS: { id: WindowOption; label: string; hours: number | null }[] = [
  { id: '24h', label: 'Last 24h', hours: 24 },
  { id: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { id: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { id: 'all', label: 'All time', hours: null },
];

export const ATTENTION_STATUSES = ['FAILED', 'ROLLED_BACK', 'ROLLBACK_FAILED', 'INCOMPATIBLE'];

export const MIGRATION_STATUSES: MigrationStatus[] = [
  'PENDING',
  'PRE_FLIGHT_PASSED',
  'IDENTITY_REPLICATED',
  'ENGINE_PROVISIONED',
  'COMPLETED',
  'FAILED',
  'ROLLED_BACK',
  'ROLLBACK_FAILED',
  'CLEANED_UP',
  'LEGACY_SERVICE_STOPPED',
  'INCOMPATIBLE',
];
